/**
 * Shared error-formatting helpers.
 *
 * Extracted from the analytics-v2 widgets (FunnelWidget, CampaignsWidget)
 * as part of CORE-1041. Callers pass the caught `unknown` from a fetch
 * .catch() and render the returned string in a user-facing error banner.
 */

import { ApiError } from '@/api/client';

/**
 * ApiError codes for which `(HTTP N)` should be suppressed even when
 * the response HAD a status. `MALFORMED_RESPONSE` (CORE-1056c helper B)
 * preserves the real 200 status on the wrapped ApiError so downstream
 * can distinguish "no response" from "bad response body" — but rendering
 * "malformed response ... (HTTP 200)" to a user pairs a failure message
 * with the success status code, which reads as a bug report. Extend
 * this set for any future client-side wrap that preserves a
 * technically-correct-but-misleading response status.
 */
const SUPPRESS_HTTP_SUFFIX_CODES: ReadonlySet<string> = new Set([
  'MALFORMED_RESPONSE',
]);

/**
 * Coerces any thrown value into a user-facing error string.
 *
 * - `ApiError` with `status > 0` and no code in
 *   `SUPPRESS_HTTP_SUFFIX_CODES` → `"message (HTTP status)"` so users
 *   can distinguish 401 from 500 without opening devtools.
 * - `ApiError` with `status === 0` (client-side wraps like
 *   `NETWORK_ERROR` from CORE-1056c helper B where no HTTP response
 *   was received) → `err.message` alone. The "(HTTP 0)" suffix would
 *   otherwise clutter an intentionally-friendly operator message with
 *   meaningless jargon.
 * - `ApiError` with `code` in `SUPPRESS_HTTP_SUFFIX_CODES` →
 *   `err.message` alone. See the set definition above for rationale.
 * - `Error` → `err.message` verbatim.
 * - anything else → `String(err)` as a last resort. Third-party libs
 *   occasionally throw plain strings, objects, or numbers, and the
 *   widget's error banner must never crash on the instanceof check nor
 *   render bare `[object Object]` without context.
 */
export function formatErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    // `err.status > 0` (not `=== 0`) preserves the round-1 defensive
    // behavior for negative / NaN / undefined statuses that shouldn't
    // occur in practice (constructor types `status: number`) but
    // would render awkwardly as "(HTTP -1)" or "(HTTP NaN)" if the
    // check narrowed to strict equality. Round-3 review catch.
    const suppressSuffix =
      !(err.status > 0) ||
      (err.code !== undefined && SUPPRESS_HTTP_SUFFIX_CODES.has(err.code));
    return suppressSuffix ? err.message : `${err.message} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}
